import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../shared/ss-select-tooltip.directive';
import * as echarts from 'echarts';
import { firstValueFrom } from 'rxjs';

import { SelfServiceChartService } from '../shared/self-service-chart.service';
import { SelfServiceFormulaService } from '../shared/self-service-formula.service';
import { SelfServiceStateService } from '../shared/self-service-state.service';
import { SelfServiceUiService } from '../shared/self-service-ui.service';
import { SelfServiceDataUtilsService } from '../shared/self-service-data-utils.service';
import { SelfServiceChartPersistenceService } from '../shared/self-service-chart-persistence.service';
import {
  ChartOptionContext,
  SelfServiceChartOptionBuilderService,
} from '../shared/self-service-chart-option-builder.service';
import { ChartPreviewComponent } from './chart-preview/chart-preview.component';
import { SelfServiceWidgetComponent } from '../shared/self-service-widget.component';
import { GenericDrillDownComponent } from '../../chart-components/generic-drill-down/generic-drill-down.component';
import { SsSelectClearDirective } from '../shared/ss-select-clear.directive';
import { SelfServiceConfigService } from '../shared/self-service-config.service';
import { SelfServiceApiConfigService } from '../shared/self-service-api-config.service';
import {
  BuilderLayoutConfig,
  BuilderStep,
} from '../shared/self-service-config.types';

import {
  AggregationType,
  ChartPreviewKind,
  ChartShapeOptions,
  ChartStyle,
  ChartTypeOption,
  ColumnType,
  DatasetColumn,
  DateGranularity,
  LabelMode,
  LegendPosition,
  LineShape,
  MappingKey,
  NumberFormat,
  NumberScale,
  SavedSelfServiceChart,
  SortDirection,
  SupportedChartType,
  TooltipAxisPointer,
  TooltipTrigger,
  TopNDirection,
  UploadedDataset,
  WidgetConfig,
  WidgetFormField,
  WidgetTableColumn,
  isWidgetPreviewKind,
} from '../shared/self-service-types';

/**
 * Chart-builder stage extracted from SelfServiceVisualizationComponent.
 *
 * Owns the chart's LIVE working state (mapping, style, selected type, series
 * field, filter metadata, preview option). Cross-stage state — dataset, chart
 * types, custom aggregations, saved charts, active saved chart id — is read from
 * and written to SelfServiceStateService. Pure data/format helpers come from
 * SelfServiceDataUtilsService; the ECharts option is produced by
 * SelfServiceChartOptionBuilderService and rendered by ChartPreviewComponent.
 */
@Component({
  selector: 'app-chart-builder',
  standalone: true,
  // Default change detection (not OnPush): chart state is updated in async
  // callbacks (save, dataset effect) and read via signal-getters; OnPush would
  // leave the preview/controls stale until the next interaction. Matches the
  // original monolith's default CD behavior.
  imports: [
    CommonModule,
    FormsModule,
    MatStepperModule,
    MatButtonModule,
    NgSelectModule,
    SsSelectTooltipDirective,
    MatIconModule,
    MatTooltipModule,
    ChartPreviewComponent,
    SelfServiceWidgetComponent,
    GenericDrillDownComponent,
    SsSelectClearDirective,
  ],
  templateUrl: './chart-builder.component.html',
  styleUrl: './chart-builder.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ChartBuilderComponent implements OnInit {
  @ViewChild('chartStepper', { static: false })
  chartStepper?: MatStepper;

  // ── Shared services ──────────────────────────────────────────────────────
  protected readonly state = inject(SelfServiceStateService);
  private readonly ui = inject(SelfServiceUiService);
  private readonly dataUtils = inject(SelfServiceDataUtilsService);
  private readonly chartOptionBuilder = inject(SelfServiceChartOptionBuilderService);
  private readonly chartService = inject(SelfServiceChartService);
  private readonly formulaService = inject(SelfServiceFormulaService);
  private readonly persistence = inject(SelfServiceChartPersistenceService);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly apiConfig = inject(SelfServiceApiConfigService);

  // ── JSON-driven layout (see JSON_CONFIG_PLAN.md) ─────────────────────────
  /** Registry of step-body template ids the template exposes. The JSON layout
   *  may only reference these; unknown ids are dropped with a console warning. */
  static readonly STEP_FIELD_IDS: ReadonlySet<string> = new Set<string>([
    'chartTypePicker',
    'mapFields',
    'styling',
    'save',
  ]);

  /** Built-in default layout — identical step order/labels to the original
   *  hardcoded stepper. Used when no `chart-builder.json` is present so the
   *  builder renders unchanged without any config. */
  private static readonly DEFAULT_LAYOUT: BuilderLayoutConfig = {
    version: '1.0',
    builder: 'chart',
    stepperOrientation: 'horizontal',
    linear: false,
    steps: [
      { id: 'chart-type', label: 'Chart type', sections: [{ id: 'chart-type', fields: [{ field: 'chartTypePicker' }] }] },
      { id: 'map-fields', label: 'Map fields', sections: [{ id: 'map-fields', fields: [{ field: 'mapFields' }] }] },
      { id: 'styling', label: 'Formatting & Styling', sections: [{ id: 'styling', fields: [{ field: 'styling' }] }] },
      { id: 'save', label: 'Save', sections: [{ id: 'save', fields: [{ field: 'save' }] }] },
    ],
  };

  /** Active layout (default until the JSON config resolves in ngOnInit). */
  layout: BuilderLayoutConfig = ChartBuilderComponent.DEFAULT_LAYOUT;

  // ── Events the shell handles (cross-stage actions the chart-builder requests
  //    but does not own). ────────────────────────────────────────────────────
  /** Ask the shell to add the given saved chart to the dashboard draft. */
  readonly addToDashboard = output<SavedSelfServiceChart>();
  /** Ask the shell to switch the active view (e.g. to 'dashboards'). */
  readonly viewModeChange = output<'data' | 'charts' | 'dashboards'>();
  /** Tell the shell the dashboard needs a re-render / config refresh
   *  (after a saved chart used on the dashboard changed). */
  readonly dashboardNeedsRefresh = output<void>();

  /** When the shell routes an "edit this chart" request (from the dashboard),
   *  it sets this input; the builder loads that chart. */
  readonly chartToEdit = input<SavedSelfServiceChart | null>(null);

  // ── Template-facing accessors for shared signal state ───────────────────────
  // The moved template binds bare names (dataset, chartTypes, savedCharts, ...);
  // these getters delegate to SelfServiceStateService.
  get dataset(): UploadedDataset | undefined { return this.state.dataset(); }
  get chartTypes(): ChartTypeOption[] { return this.state.chartTypes(); }
  get savedCharts(): SavedSelfServiceChart[] { return this.state.savedCharts(); }
  // activeSavedChartId getter is defined below (near activeSavedChart).

  // Chart types whose components consume a { category, series:[{name,data}] }
  // shape. Kept in sync with CATEGORY_SERIES_CHART_TYPES in self-service-data.utils.ts.
  private static readonly CATEGORY_SERIES_CHART_TYPES = new Set<string>([
    'stack-bar-chart', 'generic-stack-bar-chart', 'stack-bar-chart-generic',
    'mixed-stackbar-chart', 'clustered-bar-chart', 'stacked-100-bar-chart',
    'stacked-line-chart', 'stacked-area-chart', 'combined-chart', 'bar-line',
    'mbar-mline', 'multi-level-comparison-chart',
  ]);
  // Bespoke shapes handled separately from the category-series union.
  private static readonly LINE_COLUMN_CHART_TYPES = new Set<string>(['line-column-chart']);
  private static readonly COMBO_CHART_TYPES = new Set<string>(['multi-series-bar-line-combo-chart']);
  // Reads { categories, series } but must NOT receive a `bar` field (which would
  // trigger its domain-specific cummins/non-cummins path).
  private static readonly HORIZONTAL_STACK_CHART_TYPES = new Set<string>([
    'stacked-horizontal-bar-chart', 'horizontal-bar-chart-segment',
  ]);
  // Plain waterfall: [Placeholder, Increase, Decrease] ECharts series.
  private static readonly WATERFALL_CHART_TYPES = new Set<string>(['waterfall-chart']);
  // Waterfall-stack renders generic { category, series:[{name,data}] }.
  private static readonly WATERFALL_STACK_CHART_TYPES = new Set<string>(['waterfall-stack']);

  readonly maxPreviewRows = 5;
  readonly builtinAggregations: Array<{ label: string; value: AggregationType }> = [
    { label: 'Sum', value: 'sum' },
    { label: 'Average', value: 'avg' },
    { label: 'Count', value: 'count' },
    { label: 'Distinct Count', value: 'distinctCount' },
    { label: 'Distinct Values', value: 'distinct' },
    { label: 'Minimum', value: 'min' },
    { label: 'Maximum', value: 'max' },
    { label: 'Median', value: 'median' },
    { label: 'Range (max - min)', value: 'range' },
    { label: 'First', value: 'first' },
    { label: 'Last', value: 'last' }
  ];

  // Nullable on purpose: ng-select v15 expects null (not '') when nothing is
  // selected, otherwise the clear (X) button gets stuck in a CD round-trip
  // where Angular pushes '' back to ng-select, which re-coerces to null, and
  // the displayed value never actually clears.
  selectedChartType: SupportedChartType | null = null;

  // Object-form mirror of selectedChartType for ng-select binding. ng-select
  // v15 has a bug where bindValue + ngModel doesn't always sync null on clear
  // (the inner value-container keeps displaying the old label). Binding the
  // full ChartTypeOption object via [(ngModel)] sidesteps the lookup, and we
  // keep selectedChartType in sync from this setter.
  private _selectedChartTypeOption: ChartTypeOption | null = null;
  get selectedChartTypeOption(): ChartTypeOption | null { return this._selectedChartTypeOption; }
  set selectedChartTypeOption(option: ChartTypeOption | null) {
    this._selectedChartTypeOption = option;
    this.selectedChartType = option?.value ?? null;
  }

  selectedAggregation: AggregationType = 'sum';
  mapping: Partial<Record<MappingKey, string>> = {};
  seriesField = '';
  sortBy = '';
  sortDir: SortDirection = 'asc';
  topN: number | null = null;
  topNDirection: TopNDirection = 'top';
  dateGranularity: DateGranularity = 'none';
  tooltipColumns: string[] = [];
  comparisonColumn = '';
  referenceValue: number | null = null;
  /** Live settings for interactive widget chart types (input-box, slider, …).
   *  Reset whenever the selected widget type changes. */
  widgetConfig: WidgetConfig = {};
  readonly buttonStyleOptions: Array<{ label: string; value: 'primary' | 'secondary' | 'stroked' }> = [
    { label: 'Primary', value: 'primary' },
    { label: 'Secondary', value: 'secondary' },
    { label: 'Outlined', value: 'stroked' },
  ];
  readonly widgetFieldTypeOptions: Array<{ label: string; value: WidgetFormField['type'] }> = [
    { label: 'Text', value: 'text' },
    { label: 'Number', value: 'number' },
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'tel' },
    { label: 'Text area', value: 'textarea' },
    { label: 'Dropdown', value: 'select' },
  ];
  private readonly sortDirectionOptions_DEFAULT: Array<{ label: string; value: SortDirection }> = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' },
  ];
  private readonly topNDirectionOptions_DEFAULT: Array<{ label: string; value: TopNDirection }> = [
    { label: 'Top', value: 'top' },
    { label: 'Bottom', value: 'bottom' },
  ];
  private readonly dateGranularityOptions_DEFAULT: Array<{ label: string; value: DateGranularity }> = [
    { label: 'None', value: 'none' },
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Quarter', value: 'quarter' },
    { label: 'Year', value: 'year' },
  ];
  chartStyle: ChartStyle = this.createDefaultStyle();
  chartName = '';
  filterKey = '';
  filterListener: string[] = [];
  filterListenerCustom: string[] = [];
  filterListenerCustomText = '';

  private readonly palettes_DEFAULT: Array<{ key: string; label: string; colors: string[] }> = [
    { key: 'default', label: 'Default', colors: ['#1f6feb', '#0f9f8f', '#8657d6', '#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#ec4899'] },
    { key: 'warm', label: 'Warm', colors: ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#dc2626', '#b45309', '#9333ea', '#c026d3'] },
    { key: 'cool', label: 'Cool', colors: ['#0ea5e9', '#06b6d4', '#0891b2', '#0284c7', '#2563eb', '#1d4ed8', '#4f46e5', '#7c3aed'] },
    { key: 'vibrant', label: 'Vibrant', colors: ['#ec4899', '#06b6d4', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#0ea5e9', '#84cc16'] },
    { key: 'monochrome', label: 'Monochrome', colors: ['#1e293b', '#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1', '#0f172a', '#020617'] },
  ];
  private readonly numberFormats_DEFAULT: Array<{ value: NumberFormat; label: string }> = [
    { value: 'plain', label: 'Plain (1234)' },
    { value: 'thousands', label: 'Thousands (1,234)' },
    { value: 'percent', label: 'Percent (12%)' },
    { value: 'currency', label: 'Currency ($1,234)' },
  ];
  readonly numberScales: Array<{ value: NumberScale; label: string }> = [
    { value: 'none', label: 'None' },
    { value: 'compact', label: 'Auto (K / M / B)' },
    { value: 'millions', label: 'Millions (M)' },
    { value: 'billions', label: 'Billions (B)' },
  ];
  readonly lineShapeOptions: Array<{ value: LineShape; label: string }> = [
    { value: 'curved', label: 'Curved' },
    { value: 'straight', label: 'Straight' },
  ];
  private readonly legendPositions_DEFAULT: Array<{ value: LegendPosition; label: string }> = [
    { value: 'top', label: 'Top' },
    { value: 'bottom', label: 'Bottom' },
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
  ];
  private readonly labelModes_DEFAULT: Array<{ value: LabelMode; label: string }> = [
    { value: 'value', label: 'Value' },
    { value: 'percent', label: 'Percent' },
    { value: 'name-value', label: 'Name + Value' },
    { value: 'name-percent', label: 'Name + Percent' },
  ];
  private readonly tooltipTriggers_DEFAULT: Array<{ value: TooltipTrigger; label: string }> = [
    { value: 'auto', label: 'Auto (per chart type)' },
    { value: 'axis', label: 'Axis (whole category)' },
    { value: 'item', label: 'Item (single point/slice)' },
  ];
  private readonly axisPointerStyles_DEFAULT: Array<{ value: TooltipAxisPointer; label: string }> = [
    { value: 'none', label: 'None' },
    { value: 'line', label: 'Vertical line' },
    { value: 'cross', label: 'Crosshair' },
    { value: 'shadow', label: 'Shadow band' },
  ];
  private readonly tooltipPresets_DEFAULT: Array<{ key: string; label: string; background: string; textColor: string; borderColor: string }> = [
    { key: 'dark',    label: 'Dark',    background: '#1e293b', textColor: '#f8fafc', borderColor: '#334155' },
    { key: 'light',   label: 'Light',   background: '#ffffff', textColor: '#0f172a', borderColor: '#cbd5e1' },
    { key: 'navy',    label: 'Navy',    background: '#0f172a', textColor: '#e0f2fe', borderColor: '#1e3a8a' },
    { key: 'amber',   label: 'Amber',   background: '#fff7ed', textColor: '#7c2d12', borderColor: '#fdba74' },
    { key: 'emerald', label: 'Emerald', background: '#064e3b', textColor: '#ecfdf5', borderColor: '#10b981' },
  ];

  validationMessage = '';

  // Keep local message fields because the monolith bound/relayed them; mirror
  // the side-effect through the shared UI service (snackbar feedback).
  private _errorMessage = '';
  get errorMessage(): string { return this._errorMessage; }
  set errorMessage(val: string) {
    this._errorMessage = val;
    this.ui.showError(val);
  }

  private _saveMessage = '';
  get saveMessage(): string { return this._saveMessage; }
  set saveMessage(val: string) {
    this._saveMessage = val;
    this.ui.showSuccess(val);
  }

  generatedConfig = '';

  // ECharts option for the chart-builder preview. The ChartPreviewComponent
  // child owns the actual ECharts instance; we just feed it the option.
  previewOption: echarts.EChartsOption | null = null;
  private currentShapeOptions: ChartShapeOptions | null = null;

  constructor() {
    // Replicate the monolith's data-upload / entity-selection auto-default
    // behavior: when a dataset becomes available and the builder hasn't been
    // populated yet (empty mapping), seed sensible defaults. Guarded so it does
    // not clobber a chart the user is actively editing (mapping already set) nor
    // a loaded saved chart (activeSavedChartId set).
    effect(() => {
      const dataset = this.state.dataset();
      if (!dataset) {
        return;
      }
      if (Object.keys(this.mapping).length > 0) {
        return;
      }
      if (this.state.activeSavedChartId()) {
        return;
      }
      // Widgets are data-less; don't seed data-chart defaults or overwrite the
      // widget's name with a "Bar Chart - ..." style default.
      if (this.isWidgetPreview) {
        return;
      }
      this.mapping = this.createDefaultMapping();
      this.applyDefaultSeriesField();
      this.autoDefaultFilterKey();
      this.chartName = this.createDefaultChartName(dataset.fileName);
      this.renderPreview();
    });

    // When the shell routes an "edit chart" request from the dashboard, load it.
    effect(() => {
      const chart = this.chartToEdit();
      if (chart) {
        this.loadSavedChart(chart);
      }
    });
  }

  ngOnInit(): void {
    // Load the JSON layout; falls back to DEFAULT_LAYOUT on any failure so the
    // stepper always renders. Field-id validation warnings are surfaced to the
    // console (non-fatal — unknown step ids are simply skipped at render time).
    this.configService
      .loadBuilderLayout('chart', ChartBuilderComponent.DEFAULT_LAYOUT)
      .subscribe((layout) => {
        const validation = this.configService.validateLayout(
          layout,
          ChartBuilderComponent.STEP_FIELD_IDS,
        );
        if (validation.unknownFields.length) {
          console.warn(
            '[chart-builder] config references unknown field ids; they will be skipped:',
            validation.unknownFields,
          );
        }
        this.layout = layout;
        this.apiConfig.applyFromLayout(layout);
        this.applyConfigChartTypes(layout);
        // Seed the default aggregation from config when the user hasn't started
        // a chart yet (no mapping) and isn't editing a saved one.
        if (!Object.keys(this.mapping).length && !this.state.activeSavedChartId()) {
          this.selectedAggregation = this.defaultAggregation;
        }
      });
  }

  /** When the layout config supplies `options.chartTypes`, use it as the chart
   *  registry (overriding the shell's hardcoded/API list). Absent → no change,
   *  so the existing list stands. */
  private applyConfigChartTypes(layout: BuilderLayoutConfig): void {
    const types = layout.options?.chartTypes;
    if (Array.isArray(types) && types.length) {
      this.state.setChartTypes(types as unknown as ChartTypeOption[]);
    }
  }

  // ── Layout helpers (template-facing) ─────────────────────────────────────
  // Step bodies stay inline as direct <mat-step> content; the template asks
  // these helpers, per known step id, whether to render the step and what label
  // to use. The JSON layout drives visibility + labels.

  /** Find the configured step entry carrying a given field id. */
  private stepFor(fieldId: string): BuilderStep | undefined {
    return (this.layout.steps ?? []).find((step) =>
      (step.sections ?? []).some((section) =>
        (section.fields ?? []).some((field) => field.field === fieldId),
      ),
    );
  }

  /** Whether the step carrying `fieldId` should render (present in config, not
   *  statically hidden, predicate passes). Absent from config → hidden. */
  isStepVisible(fieldId: string): boolean {
    const step = this.stepFor(fieldId);
    if (!step) return false;
    return step.visible !== false && this.resolveVisibility(step.visibleWhen);
  }

  /** Label for the step carrying `fieldId`, from config (falls back to the id). */
  stepLabel(fieldId: string): string {
    return this.stepFor(fieldId)?.label ?? fieldId;
  }

  /** Resolve a `visibleWhen` predicate key against the builder's state. Unknown
   *  or absent keys default to visible (true). */
  private resolveVisibility(key: string | undefined): boolean {
    if (!key) return true;
    const predicates: Record<string, () => boolean> = {
      hasDataset: () => this.hasDataset,
    };
    const predicate = predicates[key];
    return predicate ? predicate() : true;
  }

  private getShapeOptionsFromState(): ChartShapeOptions {
    return {
      sortBy: this.sortBy || undefined,
      sortDir: this.sortBy ? this.sortDir : undefined,
      topN: this.topN ?? undefined,
      topNDirection: this.topN ? this.topNDirection : undefined,
      dateGranularity: this.dateGranularity !== 'none' ? this.dateGranularity : undefined,
      tooltipColumns: this.tooltipColumns.length ? [...this.tooltipColumns] : undefined,
      referenceValue: this.referenceValue ?? undefined,
    };
  }

  private getShapeOptionsFromChart(chart: SavedSelfServiceChart): ChartShapeOptions {
    return {
      sortBy: chart.sortBy || undefined,
      sortDir: chart.sortBy ? (chart.sortDir || 'asc') : undefined,
      topN: chart.topN ?? undefined,
      topNDirection: chart.topN ? (chart.topNDirection || 'top') : undefined,
      dateGranularity: chart.dateGranularity && chart.dateGranularity !== 'none' ? chart.dateGranularity : undefined,
      tooltipColumns: chart.tooltipColumns?.length ? [...chart.tooltipColumns] : undefined,
      referenceValue: chart.referenceValue ?? undefined,
    };
  }

  get selectedChart(): ChartTypeOption {
    return this.state.chartTypes().find((item) => item.value === this.selectedChartType) || this.state.chartTypes()[0];
  }

  get hasDataset(): boolean {
    return !!this.state.dataset()?.rows.length && !!this.state.dataset()?.columns.length;
  }

  get previewRows(): Record<string, string>[] {
    return this.state.dataset()?.rows.slice(0, this.maxPreviewRows) || [];
  }

  get isMappingComplete(): boolean {
    if (!this.selectedChartType) {
      return false;
    }

    // Interactive widgets carry no data — they're always "mapping complete" once
    // a type is picked, so the preview shows immediately with no dataset.
    if (this.isWidgetPreview) {
      return true;
    }

    if (!this.hasDataset) {
      return false;
    }

    return this.selectedChart.requiredMappings.every((key) => !!this.mapping[key]);
  }

  get numberFormatSample(): string {
    return this.dataUtils.formatNumber(12345.6789, this.chartStyle);
  }

  get numberFormatNegativeSample(): string {
    return this.dataUtils.formatNumber(-987.65, this.chartStyle);
  }

  get canRenderPreview(): boolean {
    return this.isMappingComplete && !this.validationMessage;
  }

  get showAggregationControl(): boolean {
    return !!this.selectedChart?.supportsAggregation;
  }

  get canSaveChart(): boolean {
    return this.canRenderPreview && !!this.chartName.trim();
  }

  get numericColumns(): DatasetColumn[] {
    return this.state.numericColumns();
  }

  get categoryColumns(): DatasetColumn[] {
    return this.state.categoryColumns();
  }

  get allColumns(): DatasetColumn[] {
    return this.state.dataset()?.columns || [];
  }

  get currentPreviewKind(): ChartPreviewKind {
    return this.selectedChart?.previewKind || 'axis-bar';
  }

  get isKpiPreview(): boolean { return this.currentPreviewKind === 'kpi'; }
  get isTablePreview(): boolean { return this.currentPreviewKind === 'table'; }
  get isDrillDownPreview(): boolean { return this.currentPreviewKind === 'generic-drill-down'; }
  get isWidgetPreview(): boolean { return isWidgetPreviewKind(this.currentPreviewKind); }
  get isEChartsPreview(): boolean {
    return !this.isKpiPreview && !this.isTablePreview && !this.isDrillDownPreview && !this.isWidgetPreview;
  }

  /**
   * Builder preview for the drill-down: hands the live GenericDrillDownComponent
   * a self-service `data` object so the step-2 preview shows the exact same
   * interactive tree as the arrange view and the published dashboard. Memoized
   * so the @Input reference is stable (rebuilding each CD tick would make the
   * component re-fetch endlessly).
   */
  private _drillDownPreviewData: Record<string, unknown> | null = null;
  private _drillDownPreviewKey = '';
  get drillDownPreviewData(): Record<string, unknown> {
    const dataset = this.state.dataset();
    const rows = dataset?.rows || [];
    const columns = (dataset?.columns || []).map((c) => ({ name: c.name, type: c.type }));
    const columnNames = columns.map((c) => c.name);
    const key = `${rows.length}|${columnNames.join(',')}`;
    if (this._drillDownPreviewData && this._drillDownPreviewKey === key) {
      return this._drillDownPreviewData;
    }
    const widgetId = 'ss-dd-preview';
    this._drillDownPreviewKey = key;
    this._drillDownPreviewData = {
      widgetId,
      type: 'generic-drill-down',
      apiUrl: 'self-service://local',
      // Explicit height so the component doesn't fall back to the 20vh default
      // (which squeezes the tree to ~2 rows). Fills the preview card instead.
      height: '400px',
      columnLabels: Object.fromEntries(columnNames.map((n) => [n, n])),
      selfService: {
        rawRows: rows,
        columns,
        chartType: 'generic-drill-down',
        categoryKey: '',
        valueKey: '',
        tableColumns: columnNames,
        selfWidgetId: widgetId,
      },
    };
    return this._drillDownPreviewData;
  }

  // ── Widget config helpers (interactive widget chart types) ────────────────
  /** True for a slider-family widget (shows min/max/step controls). */
  get isSliderWidget(): boolean {
    const k = this.currentPreviewKind;
    return k === 'slider' || k === 'slider-range' || k === 'range-bidirectional-slider';
  }

  addWidgetFormField(): void {
    const fields = this.widgetConfig.fields ? [...this.widgetConfig.fields] : [];
    const idx = fields.length + 1;
    fields.push({ key: `field_${idx}`, label: `Field ${idx}`, type: 'text', required: false });
    this.widgetConfig = { ...this.widgetConfig, fields };
  }

  removeWidgetFormField(index: number): void {
    const fields = (this.widgetConfig.fields || []).filter((_, i) => i !== index);
    this.widgetConfig = { ...this.widgetConfig, fields };
  }

  updateWidgetFormField(index: number, patch: Partial<WidgetFormField>): void {
    const fields = (this.widgetConfig.fields || []).map((f, i) => (i === index ? { ...f, ...patch } : f));
    this.widgetConfig = { ...this.widgetConfig, fields };
  }

  /** Comma-separated options string ↔ string[] for a select form field. */
  fieldOptionsText(field: WidgetFormField): string {
    return (field.options || []).join(', ');
  }

  setFieldOptionsText(index: number, text: string): void {
    const options = text.split(',').map((s) => s.trim()).filter(Boolean);
    this.updateWidgetFormField(index, { options });
  }

  addWidgetColumn(): void {
    const columns = this.widgetConfig.columns ? [...this.widgetConfig.columns] : [];
    const idx = columns.length + 1;
    columns.push({ key: `col_${idx}`, label: `Column ${idx}`, type: 'text' });
    this.widgetConfig = { ...this.widgetConfig, columns };
  }

  removeWidgetColumn(index: number): void {
    const columns = (this.widgetConfig.columns || []).filter((_, i) => i !== index);
    this.widgetConfig = { ...this.widgetConfig, columns };
  }

  updateWidgetColumn(index: number, patch: Partial<WidgetTableColumn>): void {
    const columns = (this.widgetConfig.columns || []).map((c, i) => (i === index ? { ...c, ...patch } : c));
    this.widgetConfig = { ...this.widgetConfig, columns };
  }

  /** New object reference so the preview's signal input picks up the change. */
  onWidgetConfigChange(): void {
    this.widgetConfig = { ...this.widgetConfig };
    this.saveMessage = '';
  }

  /** Seed per-widget default settings the first time a widget type is picked. */
  private seedWidgetDefaults(): void {
    const kind = this.currentPreviewKind;
    const base: WidgetConfig = { label: this.selectedChart?.label || '' };
    switch (kind) {
      case 'input-box':
        this.widgetConfig = { ...base, placeholder: 'Enter a value' };
        break;
      case 'slider':
      case 'range-bidirectional-slider':
        this.widgetConfig = { ...base, min: 0, max: 100, step: 1, defaultValue: 50 };
        break;
      case 'slider-range':
        this.widgetConfig = { ...base, min: 0, max: 100, step: 1, rangeStart: 20, rangeEnd: 80 };
        break;
      case 'button':
        this.widgetConfig = { ...base, buttonLabel: 'Submit', buttonStyle: 'primary' };
        break;
      case 'notes':
        this.widgetConfig = { ...base, notesText: '' };
        break;
      case 'upload-file':
        this.widgetConfig = { ...base, accept: '.csv,.xlsx', multiple: false };
        break;
      case 'configurable-form':
        this.widgetConfig = {
          ...base,
          submitLabel: 'Submit',
          fields: [{ key: 'field_1', label: 'Field 1', type: 'text', required: false }],
        };
        break;
      case 'editable-table':
        this.widgetConfig = {
          ...base,
          columns: [
            { key: 'col_1', label: 'Column 1', type: 'text' },
            { key: 'col_2', label: 'Column 2', type: 'number' },
          ],
          rows: [],
        };
        break;
      default:
        this.widgetConfig = base;
    }
  }

  get supportsSeriesField(): boolean {
    const kind = this.currentPreviewKind;
    // Charts with multi-series rendering — including the stacked variants which
    // need a series column to actually stack, and bar-line / bubble variants
    // which group series by this column. Gantt uses it as task-category labels.
    return (
      kind === 'axis-bar' ||
      kind === 'axis-line' ||
      kind === 'area' ||
      kind === 'horizontal-bar' ||
      kind === 'scatter' ||
      kind === 'stacked-bar' ||
      kind === 'stacked-horizontal-bar' ||
      kind === 'stacked-line' ||
      kind === 'stacked-area' ||
      kind === 'bar-line' ||
      kind === 'waterfall-stack' ||
      kind === 'bubble' ||
      kind === 'quadrant-bubble' ||
      kind === 'gantt'
    );
  }

  get optionalMappings(): MappingKey[] {
    return this.selectedChart?.optionalMappings || [];
  }

  get supportsSort(): boolean { return !!this.selectedChart?.supportsSort; }
  get supportsTopN(): boolean { return !!this.selectedChart?.supportsTopN; }
  get supportsTooltipColumns(): boolean { return !!this.selectedChart?.supportsTooltipColumns; }
  get supportsReferenceLine(): boolean { return !!this.selectedChart?.supportsReferenceLine; }
  get supportsComparison(): boolean { return !!this.selectedChart?.supportsComparison; }

  /** Whether a SECONDARY (right) value axis is in play — either an explicit
   *  yAxis2 mapping, or a bar-line/combo chart that always overlays a right-axis
   *  line. Gates the secondary number-format controls. */
  get hasSecondaryAxis(): boolean {
    if (!this.isEChartsPreview) return false;
    if (this.mapping.yAxis2) return true;
    return this.currentPreviewKind === 'bar-line';
  }

  /** Whether the chart draws line series whose shape (straight vs curved) the
   *  user can control: line/area/combo kinds, or any chart with a yAxis2 line. */
  get supportsLineShape(): boolean {
    const kind = this.currentPreviewKind;
    const lineKinds: ChartPreviewKind[] = ['axis-line', 'area', 'stacked-line', 'stacked-area', 'bar-line'];
    if (lineKinds.includes(kind)) return true;
    return !!this.mapping.yAxis2 && this.isEChartsPreview;
  }

  get supportsDateGranularity(): boolean {
    if (!this.selectedChart?.supportsDateGranularity) return false;
    const xCol = this.mapping.xAxis;
    if (!xCol) return false;
    return this.state.dataset()?.columns.find((c) => c.name === xCol)?.type === 'date';
  }

  get tooltipColumnOptions(): DatasetColumn[] {
    return this.state.dataset()?.columns || [];
  }

  get kpiBuilderDisplay(): { value: string; label: string; comparisonText: string; comparisonDirection: 'up' | 'down' | 'flat' } {
    const style = this.chartStyle;
    const dataset = this.state.dataset();
    if (!dataset || !this.mapping.value) {
      return { value: '-', label: this.chartName || 'KPI', comparisonText: '', comparisonDirection: 'flat' };
    }

    const valueKey = this.mapping.value;
    const columnType = this.getColumnType(valueKey);
    const rawValues = dataset.rows.map((row) => String(row[valueKey] ?? '').trim());
    const nonEmptyValues = rawValues.filter((value) => value !== '');
    const numbers = rawValues.map((value) => this.dataUtils.toNumber(value));
    const raw = this.dataUtils.aggregateValues(numbers, this.selectedAggregation);
    const numericResult = columnType === 'number' || ['count', 'distinctCount'].includes(this.selectedAggregation);
    const textResult = this.selectedAggregation === 'last'
      ? (nonEmptyValues[nonEmptyValues.length - 1] || '')
      : (nonEmptyValues[0] || '');
    const value = numericResult ? this.dataUtils.formatNumber(raw, style) : (textResult || '-');

    let comparisonText = '';
    let comparisonDirection: 'up' | 'down' | 'flat' = 'flat';
    if (numericResult && style.comparisonValue != null && Number.isFinite(style.comparisonValue)) {
      const target = Number(style.comparisonValue);
      const delta = raw - target;
      const pct = target !== 0 ? (delta / target) * 100 : 0;
      comparisonDirection = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
      const sign = delta > 0 ? '+' : '';
      comparisonText = `${sign}${pct.toFixed(1)}% vs ${this.dataUtils.formatNumber(target, style)}`;
    }
    return {
      value,
      label: style.titleOverride || this.chartName || valueKey,
      comparisonText,
      comparisonDirection,
    };
  }
  get tableBuilderDisplay(): { columns: DatasetColumn[]; rows: Record<string, string>[] } {
    const dataset = this.state.dataset();
    if (!dataset) {
      return { columns: [], rows: [] };
    }
    const limit = Math.max(1, Math.floor(this.chartStyle.tableRowLimit || 50));
    return { columns: dataset.columns, rows: dataset.rows.slice(0, limit) };
  }

  get currentPaletteColors(): string[] {
    return this.dataUtils.getPaletteColors(this.chartStyle.palette);
  }

  get availableListenerCharts(): SavedSelfServiceChart[] {
    return this.state.savedCharts();
  }

  // ng-select binds [items]="aggregations". A plain getter returns a NEW array
  // reference on every change-detection pass; when an option is clicked the
  // resulting CD rebuilds the array, ng-select re-syncs its item list and the
  // just-picked value fails to "stick" (the dropdown appears unselectable).
  // Memoise so the reference is stable unless the actual inputs change.
  private _aggregationsCache: Array<{ label: string; value: AggregationType; custom?: boolean }> = [];
  private _aggregationsBuiltinsRef: unknown = null;
  private _aggregationsCustomRef: unknown = null;
  get aggregations(): Array<{ label: string; value: AggregationType; custom?: boolean }> {
    // Built-in aggregations come from JSON config when provided, else the
    // hardcoded list; custom (dataset-defined) aggregations are always appended.
    const builtins =
      (this.layout.options?.aggregations as Array<{ label: string; value: AggregationType }> | undefined) ??
      this.builtinAggregations;
    const custom = this.state.customAggregations();
    // Both inputs change reference only when they genuinely change, so
    // reference-equality on the pair is a sufficient (and cheap) cache key.
    if (this._aggregationsBuiltinsRef === builtins && this._aggregationsCustomRef === custom) {
      return this._aggregationsCache;
    }
    this._aggregationsBuiltinsRef = builtins;
    this._aggregationsCustomRef = custom;
    this._aggregationsCache = [
      ...builtins,
      ...custom.map((agg) => ({ label: `${agg.label} (custom)`, value: agg.key, custom: true })),
    ];
    return this._aggregationsCache;
  }

  // ── JSON-configurable option lists ───────────────────────────────────────
  // Each getter prefers the loaded layout's `options.*` list and falls back to
  // the hardcoded *_DEFAULT below, so an absent/partial options block never
  // blanks a control. The template binds these getter names unchanged.
  get palettes(): Array<{ key: string; label: string; colors: string[] }> {
    return (this.layout.options?.palettes as typeof this.palettes_DEFAULT | undefined) ?? this.palettes_DEFAULT;
  }
  get sortDirectionOptions(): Array<{ label: string; value: SortDirection }> {
    return (this.layout.options?.sortDirections as typeof this.sortDirectionOptions_DEFAULT | undefined) ?? this.sortDirectionOptions_DEFAULT;
  }
  get topNDirectionOptions(): Array<{ label: string; value: TopNDirection }> {
    return (this.layout.options?.topNDirections as typeof this.topNDirectionOptions_DEFAULT | undefined) ?? this.topNDirectionOptions_DEFAULT;
  }
  get dateGranularityOptions(): Array<{ label: string; value: DateGranularity }> {
    return (this.layout.options?.dateGranularities as typeof this.dateGranularityOptions_DEFAULT | undefined) ?? this.dateGranularityOptions_DEFAULT;
  }
  get numberFormats(): Array<{ value: NumberFormat; label: string }> {
    return (this.layout.options?.numberFormats as typeof this.numberFormats_DEFAULT | undefined) ?? this.numberFormats_DEFAULT;
  }
  get legendPositions(): Array<{ value: LegendPosition; label: string }> {
    return (this.layout.options?.legendPositions as typeof this.legendPositions_DEFAULT | undefined) ?? this.legendPositions_DEFAULT;
  }
  get labelModes(): Array<{ value: LabelMode; label: string }> {
    return (this.layout.options?.labelModes as typeof this.labelModes_DEFAULT | undefined) ?? this.labelModes_DEFAULT;
  }
  get tooltipTriggers(): Array<{ value: TooltipTrigger; label: string }> {
    return (this.layout.options?.tooltipTriggers as typeof this.tooltipTriggers_DEFAULT | undefined) ?? this.tooltipTriggers_DEFAULT;
  }
  get axisPointerStyles(): Array<{ value: TooltipAxisPointer; label: string }> {
    return (this.layout.options?.axisPointerStyles as typeof this.axisPointerStyles_DEFAULT | undefined) ?? this.axisPointerStyles_DEFAULT;
  }
  get tooltipPresets(): Array<{ key: string; label: string; background: string; textColor: string; borderColor: string }> {
    return (this.layout.options?.tooltipPresets as typeof this.tooltipPresets_DEFAULT | undefined) ?? this.tooltipPresets_DEFAULT;
  }

  get sourceCategoryColumn(): string {
    if (this.selectedChart?.requiredMappings.includes('category')) {
      return this.mapping.category || '';
    }
    return this.mapping.xAxis || '';
  }

  get filterKeyMismatchWarning(): string {
    if (!this.filterKey || !this.sourceCategoryColumn) {
      return '';
    }
    if (this.filterKey === this.sourceCategoryColumn) {
      return '';
    }
    return `"${this.filterKey}" isn't this chart's category column ("${this.sourceCategoryColumn}"). Clicks publish the category value, so listeners may not match.`;
  }

  get listenerColumnWarnings(): string[] {
    if (!this.filterKey || !this.filterListener.length) {
      return [];
    }
    const warnings: string[] = [];
    for (const listenerId of this.filterListener) {
      const listener = this.state.savedCharts().find((chart) => chart.id === listenerId);
      if (!listener) {
        continue;
      }
      const hasColumn = listener.dataset.columns.some((column) => column.name === this.filterKey);
      if (!hasColumn) {
        warnings.push(`"${listener.name}" has no "${this.filterKey}" column — filter will no-op.`);
      }
    }
    return warnings;
  }

  get activeSavedChartId(): string {
    return this.state.activeSavedChartId();
  }

  get activeSavedChart(): SavedSelfServiceChart | undefined {
    return this.state.activeSavedChart();
  }

  // Force-null the chart type on (clear). Going through the option setter
  // keeps both selectedChartTypeOption and selectedChartType in sync.
  clearChartType(): void {
    this.selectedChartTypeOption = null;
    this.onChartTypeChange();
  }

  // ng-select v15's own (clear) does not fire reliably for the X here (the
  // mousedown target ends up being an inner span, not .ng-clear-wrapper, so the
  // library's handleMousedown never calls handleClearClick). We catch the click
  // ourselves: if it landed anywhere inside the clear button, clear the model
  // directly. This is independent of ng-select's target-class matching.
  onChartTypeChange(): void {
    this.mapping = {};
    this.seriesField = '';
    this.resetExtendedMappingOptions();
    this.errorMessage = '';
    this.validationMessage = '';
    this.saveMessage = '';
    this.generatedConfig = '';
    this.previewOption = null;

    // Interactive widgets don't use the dataset/mapping pipeline. Seed sensible
    // default settings for the chosen widget so its preview renders right away.
    if (this.isWidgetPreview) {
      this.seedWidgetDefaults();
      // Reset the name to the chosen widget's label so the saved widget reflects
      // the selected type (e.g. "Input Box", "Slider") rather than a stale chart
      // default like "Bar Chart - ...". Mirrors the data-chart branch below.
      if (!this.state.activeSavedChartId()) {
        this.chartName = this.selectedChart?.label || 'Widget';
      }
      return;
    }
    this.widgetConfig = {};

    if (this.state.dataset()) {
      this.mapping = this.createDefaultMapping();
      this.applyDefaultSeriesField();
      // Reset chart name so the preview title reflects the new chart type,
      // not the stale name from a previously-selected type.
      if (!this.state.activeSavedChartId()) {
        this.chartName = this.createDefaultChartName(this.state.dataset()!.fileName);
      }
      this.renderPreview();
    }
  }

  onMappingChange(): void {
    this.errorMessage = '';
    this.saveMessage = '';
    this.autoDefaultFilterKey();
    this.renderPreview();
  }

  private autoDefaultFilterKey(): void {
    if (!this.filterKey && this.sourceCategoryColumn) {
      this.filterKey = this.sourceCategoryColumn;
    }
  }

  onAggregationChange(): void {
    this.errorMessage = '';
    this.saveMessage = '';
    this.renderPreview();
  }

  onFilterMetadataChange(): void {
    this.saveMessage = '';
    this.renderPreview();
  }

  onFilterListenerCustomChange(): void {
    this.filterListenerCustom = this.parseFilterListenerCustomText(this.filterListenerCustomText);
    this.onFilterMetadataChange();
  }

  onChartOptionsChange(): void {
    this.errorMessage = '';
    this.saveMessage = '';
    this.renderPreview();
  }

  private resetExtendedMappingOptions(): void {
    this.sortBy = '';
    this.sortDir = 'asc';
    this.topN = null;
    this.topNDirection = 'top';
    this.dateGranularity = 'none';
    this.tooltipColumns = [];
    this.comparisonColumn = '';
    this.referenceValue = null;
  }

  onStyleChange(): void {
    this.saveMessage = '';
    this.renderPreview();
    this.scheduleDashboardRender();
  }

  onSeriesFieldChange(): void {
    this.saveMessage = '';
    this.renderPreview();
  }

  resetStyle(): void {
    this.chartStyle = this.createDefaultStyle();
    this.onStyleChange();
  }

  applyTooltipPreset(presetKey: string): void {
    const preset = this.tooltipPresets.find((p) => p.key === presetKey);
    if (!preset) return;
    this.chartStyle.tooltipBackground = preset.background;
    this.chartStyle.tooltipTextColor = preset.textColor;
    this.chartStyle.tooltipBorderColor = preset.borderColor;
    this.onStyleChange();
  }

  resolveCurrentTooltipTrigger(): 'axis' | 'item' {
    const style = this.chartStyle;
    if (style.tooltipTrigger !== 'auto') {
      return style.tooltipTrigger;
    }
    const axisKinds: ChartPreviewKind[] = ['axis-bar', 'axis-line', 'horizontal-bar', 'area'];
    return axisKinds.includes(this.currentPreviewKind) ? 'axis' : 'item';
  }

  formatValueForDisplay(value: number, style?: ChartStyle | null): string {
    return this.dataUtils.formatNumber(value, this.normalizeStyle(style));
  }

  private mergeListenerWidgetIds(
    dropdownChartIds: string[],
    customWidgetIds: string[] | undefined,
    selfWidgetId: string,
  ): string[] {
    const dropdownIds = dropdownChartIds.map((id) => this.buildWidgetId(id));
    const customIds = customWidgetIds || [];
    const merged: string[] = [];
    for (const id of [...dropdownIds, ...customIds]) {
      if (id === selfWidgetId) {
        continue;
      }
      if (!merged.includes(id)) {
        merged.push(id);
      }
    }
    return merged;
  }

  private parseFilterListenerCustomText(text: string): string[] {
    return text
      .split(/[,\n]/)
      .map((token) => token.trim())
      .filter((token) => token.length > 0)
      .map((token) => this.buildWidgetId(token))
      .filter((id, index, arr) => arr.indexOf(id) === index);
  }

  async saveChart(): Promise<void> {
    this.errorMessage = '';
    this.saveMessage = '';
    this.renderPreview();

    const isWidget = this.isWidgetPreview;
    const dataset = this.state.dataset();
    // Widgets carry no dataset — they only need a name + a picked type. Data
    // charts still require an uploaded dataset and complete mappings.
    if ((!dataset && !isWidget) || !this.canSaveChart) {
      this.saveMessage = isWidget
        ? 'Enter a widget name before saving.'
        : 'Complete chart name, upload data, and valid mappings before saving.';
      return;
    }

    const now = new Date().toISOString();
    const config = this.buildGeneratedConfig();
    const existing = this.state.savedCharts().find((chart) => chart.id === this.state.activeSavedChartId());
    const savedChart: SavedSelfServiceChart = {
      id: existing?.id || this.createId(),
      name: this.chartName.trim(),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      chartType: this.selectedChartType || '',
      aggregation: this.selectedAggregation,
      mapping: { ...this.mapping },
      seriesField: this.seriesField || undefined,
      sortBy: this.sortBy || undefined,
      sortDir: this.sortBy ? this.sortDir : undefined,
      topN: this.topN ?? undefined,
      topNDirection: this.topN ? this.topNDirection : undefined,
      dateGranularity: this.dateGranularity !== 'none' ? this.dateGranularity : undefined,
      tooltipColumns: this.tooltipColumns.length ? [...this.tooltipColumns] : undefined,
      comparisonColumn: this.comparisonColumn || undefined,
      referenceValue: this.referenceValue ?? undefined,
      style: { ...this.chartStyle },
      filterKey: this.filterKey,
      filterListener: [...this.filterListener],
      filterListenerCustom: [...this.filterListenerCustom],
      datasetId: dataset?.datasetId || this.state.rawDataset()?.datasetId,
      dataset: dataset ? this.cloneDataset(dataset) : { fileName: '', sourceType: 'csv', columns: [], rows: [] },
      config,
      widgetConfig: isWidget ? { ...this.widgetConfig } : undefined,
    };

    try {
      const persistedChart = await firstValueFrom(this.chartService.saveChart(savedChart));
      const nextCharts = existing
        ? this.state.savedCharts().map((chart) => chart.id === existing.id ? persistedChart : chart)
        : [persistedChart, ...this.state.savedCharts()];

      if (this.persistSavedCharts(nextCharts)) {
        this.state.setSavedCharts(nextCharts);
        this.state.setActiveSavedChartId(persistedChart.id);
        this.generatedConfig = JSON.stringify(config, null, 2);
        this.saveMessage = existing ? 'Chart updated.' : 'Chart saved.';
        this.updateDashboardConfig();
        // Propagate the edit into every saved dashboard that uses this chart so
        // their stored/published config reflects the new mapping/formatting/keys.
        if (existing) {
          await this.resaveDashboardsForChart(persistedChart.id);
        }
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Unable to save chart.';
    }
  }

  async saveChartAsNew(): Promise<void> {
    const wasExisting = !!this.state.activeSavedChartId();
    this.state.setActiveSavedChartId('');
    if (wasExisting) {
      this.chartName = this.chartName.trim()
        ? `${this.chartName.trim()} (copy)`
        : 'New Chart';
    }
    await this.saveChart();
  }

  loadSavedChart(chart: SavedSelfServiceChart): void {
    this.errorMessage = '';
    this.validationMessage = '';
    this.saveMessage = '';
    this.state.setActiveSavedChartId(chart.id);
    this.chartName = chart.name;
    this.selectedChartTypeOption = this.state.chartTypes().find((t) => t.value === chart.chartType) ?? null;
    this.selectedAggregation = chart.aggregation || 'sum';
    this.mapping = { ...chart.mapping };
    this.seriesField = chart.seriesField || '';
    this.sortBy = chart.sortBy || '';
    this.sortDir = chart.sortDir || 'asc';
    this.topN = chart.topN ?? null;
    this.topNDirection = chart.topNDirection || 'top';
    this.dateGranularity = chart.dateGranularity || 'none';
    this.tooltipColumns = Array.isArray(chart.tooltipColumns) ? [...chart.tooltipColumns] : [];
    this.comparisonColumn = chart.comparisonColumn || '';
    this.referenceValue = chart.referenceValue ?? null;
    this.chartStyle = this.normalizeStyle(chart.style);
    this.filterKey = chart.filterKey || '';
    this.filterListener = Array.isArray(chart.filterListener) ? [...chart.filterListener] : [];
    this.filterListenerCustom = Array.isArray(chart.filterListenerCustom) ? [...chart.filterListenerCustom] : [];
    this.filterListenerCustomText = this.filterListenerCustom.join(', ');
    this.widgetConfig = chart.widgetConfig ? { ...chart.widgetConfig } : {};
    this.state.setDataset(this.cloneDataset(chart.dataset));

    queueMicrotask(() => this.renderPreview());
  }

  async deleteSavedChart(chart: SavedSelfServiceChart, event?: Event): Promise<void> {
    event?.stopPropagation();
    try {
      await firstValueFrom(this.chartService.deleteChart(chart.id));
      const nextCharts = this.state.savedCharts().filter((item) => item.id !== chart.id);

      if (this.persistSavedCharts(nextCharts)) {
        this.state.setSavedCharts(nextCharts);
        this.removeDashboardItemsForChart(chart.id);
        if (this.state.activeSavedChartId() === chart.id) {
          this.state.setActiveSavedChartId('');
        }
        this.saveMessage = 'Saved chart deleted.';
        this.updateDashboardConfig();
        this.scheduleDashboardRender();
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Unable to delete chart.';
    }
  }

  /** Default aggregation seed — JSON `defaults.aggregation` or the built-in 'sum'. */
  private get defaultAggregation(): AggregationType {
    return (this.layout.defaults?.aggregation as AggregationType | undefined) ?? 'sum';
  }

  startNewChart(options: { preserveDataset?: boolean } = {}): void {
    this.selectedChartTypeOption = null;
    this.selectedAggregation = this.defaultAggregation;
    if (!options.preserveDataset) {
      this.state.setDataset(undefined);
      this.state.setRawDataset(undefined);
    }
    this.mapping = {};
    this.seriesField = '';
    this.resetExtendedMappingOptions();
    this.chartStyle = this.createDefaultStyle();
    this.chartName = '';
    this.filterKey = '';
    this.filterListener = [];
    this.filterListenerCustom = [];
    this.filterListenerCustomText = '';
    this.state.setActiveSavedChartId('');
    this.errorMessage = '';
    this.validationMessage = '';
    this.saveMessage = '';
    this.generatedConfig = '';
    this.previewOption = null;
    queueMicrotask(() => {
      if (this.chartStepper) {
        this.chartStepper.selectedIndex = 0;
      }
    });
  }
  addActiveChartToDashboard(): void {
    const chart = this.activeSavedChart;
    if (!chart) {
      this.saveMessage = 'Select or save a chart before adding it to the dashboard draft.';
      return;
    }

    this.addChartToDashboard(chart);
    this.setViewMode('dashboards');
  }

  private createDefaultMapping(): Partial<Record<MappingKey, string>> {
    // Prefer real category columns and measures, skipping id-like unique keys
    // (e.g. an `id` column) which would otherwise be picked as the x-axis and
    // render one meaningless bar per row.
    const firstCategory = this.bestCategoryColumn();
    const firstNumber = this.bestNumericColumn([], false);

    if (this.selectedChart.requiredMappings.includes('category')) {
      // Prefer a non-year, non-id numeric column for the value axis.
      const bestValueCol = this.bestNumericColumn();
      return {
        category: firstCategory,
        value: bestValueCol,
      };
    }

    // KPI Card — only needs a single value column, no category/xAxis.
    if (this.selectedChart.requiredMappings.includes('value')) {
      const bestValueCol = this.bestNumericColumn();
      return { value: bestValueCol };
    }

    const previewKind = this.selectedChart?.previewKind;
    if (previewKind === 'scatter' || previewKind === 'bubble' || previewKind === 'quadrant-bubble') {
      // Skip year-like AND id-like columns so bubbles aren't all clustered at
      // x˜2020 or spread along a meaningless id axis — pick two distinct
      // numeric measures instead.
      const bestX = this.bestNumericColumn() || firstNumber;
      const bestY = this.bestNumericColumn(bestX ? [bestX] : []) || firstNumber;
      return { xAxis: bestX, yAxis: bestY };
    }

    if (previewKind === 'gantt') {
      // Prefer year-like columns for the timeline so the Gantt shows a proper
      // date range. If a year column exists, use it for both start and end —
      // buildGanttOption will auto-increment end by 1 to give each row a
      // 1-unit duration, producing a timeline like 2020?2024.
      const yearCol = this.numericColumns.find(c => this.isLikelyYearColumn(c.name))?.name;
      if (yearCol) {
        return { xAxis: yearCol, yAxis: yearCol };
      }
      // No year column — use two distinct numeric measures (skip id columns).
      const ganttX = this.bestNumericColumn([], false) || firstNumber;
      const ganttY = this.bestNumericColumn(ganttX ? [ganttX] : [], false) || ganttX;
      return { xAxis: ganttX, yAxis: ganttY };
    }

    // For axis charts prefer a non-year, non-id numeric column for yAxis so we
    // don't accidentally render "sum of Year" or "sum of id" as the bar height.
    const bestYAxis = this.bestNumericColumn();

    const chartType = this.selectedChartType || '';

    // Multi-Level Comparison: set xAxis2 to a second category column so the
    // preview shows a proper two-level X-axis hierarchy rather than plain bars.
    if (chartType === 'multi-level-comparison-chart') {
      const secondCategory = this.bestCategoryColumn(firstCategory ? [firstCategory] : []);
      return {
        xAxis: firstCategory,
        xAxis2: secondCategory,
        yAxis: bestYAxis,
      };
    }

    // For bar-line chart types that are NOT mixed/stacked variants: auto-set
    // yAxis2 to a second numeric metric so the preview shows the characteristic
    // dual-axis bars + line pattern instead of stacked multi-series bars.
    if (previewKind === 'bar-line') {
      const STACKED_BAR_LINE = ['mixed-stackbar-chart', 'mbar-mline'];
      if (!STACKED_BAR_LINE.includes(chartType)) {
        const secondMetric = this.bestNumericColumn(bestYAxis ? [bestYAxis] : []);
        if (secondMetric && secondMetric !== bestYAxis) {
          return {
            xAxis: firstCategory,
            yAxis: bestYAxis,
            yAxis2: secondMetric,
          };
        }
      }
    }

    return {
      xAxis: firstCategory,
      yAxis: bestYAxis,
    };
  }

  private isLikelyYearColumn(colName: string): boolean {
    const dataset = this.state.dataset();
    if (!dataset) return false;
    const sample = dataset.rows.slice(0, 20).map(r => Number(r[colName]));
    const valid = sample.filter(v => Number.isFinite(v) && v !== 0);
    if (!valid.length) return false;
    return valid.every(v => v >= 1900 && v <= 2100);
  }

  /** True for unique-key columns like `id` — all-numeric, every value distinct
   *  (≈ one per row). Such columns make terrible x-axis / category / value
   *  picks (one bar per row, nothing to aggregate or stack), so the default
   *  mapping skips them. Name-based heuristics (`id`, `*_id`, `uuid`, `key`,
   *  `code`) catch the common cases even when a dataset has duplicate ids. */
  private isLikelyIdColumn(colName: string): boolean {
    const dataset = this.state.dataset();
    if (!dataset) return false;
    const col = dataset.columns.find((c) => c.name === colName);
    if (!col || col.type !== 'number') return false;

    const normalized = colName.trim().toLowerCase();
    const nameLooksLikeId =
      normalized === 'id' ||
      normalized === 'uuid' ||
      normalized === 'guid' ||
      normalized === 'key' ||
      normalized === 'index' ||
      normalized === 'sno' ||
      normalized === 'srno' ||
      /(^|[_\s])(id|key|code|no)$/.test(normalized);

    const values = dataset.rows.map((r) => r[colName]).filter((v) => v != null && String(v).trim() !== '');
    if (!values.length) return false;
    const distinct = new Set(values.map((v) => String(v).trim()));
    const uniqueRatio = distinct.size / values.length;
    // Treat as an id when values are (near-)unique, or the name screams id and
    // the column is reasonably high-cardinality.
    return uniqueRatio >= 0.9 || (nameLooksLikeId && uniqueRatio >= 0.6);
  }

  /** First category-eligible column that isn't an id-like unique key. Falls back
   *  to the raw first category column when every candidate looks like an id. */
  private bestCategoryColumn(exclude: string[] = []): string | undefined {
    const usable = this.categoryColumns.filter(
      (c) => !exclude.includes(c.name) && !this.isLikelyIdColumn(c.name),
    );
    return usable[0]?.name
      ?? this.categoryColumns.find((c) => !exclude.includes(c.name))?.name
      ?? this.categoryColumns[0]?.name;
  }

  /** First numeric column suitable as a measure: skips id-like and (optionally)
   *  year-like columns so we don't render "sum of id" or "sum of Year". */
  private bestNumericColumn(exclude: string[] = [], skipYear = true): string | undefined {
    const usable = this.numericColumns.filter(
      (c) =>
        !exclude.includes(c.name) &&
        !this.isLikelyIdColumn(c.name) &&
        (!skipYear || !this.isLikelyYearColumn(c.name)),
    );
    if (usable[0]) return usable[0].name;
    // Relax the year filter before falling all the way back to id columns.
    const noId = this.numericColumns.filter((c) => !exclude.includes(c.name) && !this.isLikelyIdColumn(c.name));
    if (noId[0]) return noId[0].name;
    return this.numericColumns.find((c) => !exclude.includes(c.name))?.name ?? this.numericColumns[0]?.name;
  }

  /** Auto-pick a sensible default for the Series / split-by field — same idea
   *  as createDefaultMapping for xAxis/yAxis, but only for chart types that
   *  genuinely need multi-series to render correctly (stacked variants,
   *  bar-line combos, bubble). For optional cases (plain bar, line, area,
   *  scatter) the user picks if they want — we don't auto-fill. */
  private applyDefaultSeriesField(): void {
    const dataset = this.state.dataset();
    if (!dataset || !this.supportsSeriesField) {
      this.seriesField = '';
      return;
    }

    // Preserve a valid existing selection (don't overwrite user's pick if it's
    // still a real column in the current dataset).
    if (
      this.seriesField &&
      dataset.columns.some((c) => c.name === this.seriesField)
    ) {
      return;
    }

    if (!this.requiresSeriesFieldDefault()) {
      this.seriesField = '';
      return;
    }

    const usedX = this.mapping.xAxis || this.mapping.category || '';
    const usedY = this.mapping.yAxis || this.mapping.value || '';

    // For multi-level comparison: match seriesField to xAxis2 (same column) so
    // bars are both sub-labelled on the second X axis AND colour-coded.
    if (this.selectedChartType === 'multi-level-comparison-chart' && this.mapping.xAxis2) {
      this.seriesField = this.mapping.xAxis2;
      return;
    }

    // A good series splitter is a low-to-moderate cardinality column whose values
    // actually vary — a column with a single distinct value (e.g. all rows are
    // "North America") produces just one series and the chart looks unstacked.
    // Score string columns by how many distinct values they hold and pick the
    // most "groupable" one that isn't already the x/y axis or an id-like key.
    const distinctCount = (name: string): number => {
      const seen = new Set<string>();
      for (const row of dataset.rows) {
        const v = row[name];
        if (v != null && String(v).trim() !== '') seen.add(String(v).trim());
        if (seen.size > 50) break; // cap — anything this high is too granular
      }
      return seen.size;
    };
    const isGroupable = (name: string): boolean => {
      if (name === usedX || name === usedY) return false;
      if (this.isLikelyIdColumn(name)) return false;
      const n = distinctCount(name);
      return n >= 2 && n <= 30; // varies enough to stack, not so much it's a key
    };

    // Prefer string columns (most natural for grouping); among those, the one
    // with the most distinct (but still bounded) values gives the richest stack.
    const stringCandidate = dataset.columns
      .filter((c) => c.type === 'string' && isGroupable(c.name))
      .sort((a, b) => distinctCount(b.name) - distinctCount(a.name))[0];
    if (stringCandidate) {
      this.seriesField = stringCandidate.name;
      return;
    }

    // Year-like numeric columns (2020, 2021 …) are excellent series splitters
    // for clustered/multi-level charts even though they're stored as numbers.
    const yearCandidate = dataset.columns.find(
      (c) => c.type === 'number' && c.name !== usedX && c.name !== usedY && this.isLikelyYearColumn(c.name),
    );
    if (yearCandidate) {
      this.seriesField = yearCandidate.name;
      return;
    }

    // Last resort: any non-numeric, groupable column (boolean/date buckets).
    const anyGroupable = dataset.columns.find(
      (c) => c.type !== 'empty' && c.type !== 'number' && isGroupable(c.name),
    );
    if (anyGroupable) {
      this.seriesField = anyGroupable.name;
      return;
    }

    // Nothing varies enough to stack — fall back to the original lenient pick so
    // the chart at least colour-codes rather than silently dropping the series.
    const anyCandidate = dataset.columns.find(
      (c) => c.type !== 'empty' && c.type !== 'number' && c.name !== usedX && c.name !== usedY && !this.isLikelyIdColumn(c.name),
    );
    this.seriesField = anyCandidate ? anyCandidate.name : '';
  }

  private requiresSeriesFieldDefault(): boolean {
    const kind = this.currentPreviewKind;
    const chartType = this.selectedChartType || '';
    // These axis-bar chart types render as plain bars without a series split —
    // auto-default the series field so they show meaningful clustering/comparison.
    const needsSeriesForClustering = [
      'clustered-bar-chart',
      'comparison-bar-chart',
      'multi-level-comparison-chart',
      'named-bar-chart-with-division',
    ];
    // Mixed/multi-series bar-line variants need stacked series to show their
    // characteristic mixed-stack appearance. Plain bar-line chart types
    // instead get a yAxis2 auto-set (in createDefaultMapping) so they render
    // as single-metric bars + line — no seriesField needed there.
    const stackedBarLineTypes = ['mixed-stackbar-chart', 'mbar-mline'];
    return (
      needsSeriesForClustering.includes(chartType) ||
      stackedBarLineTypes.includes(chartType) ||
      kind === 'stacked-bar' ||
      kind === 'stacked-horizontal-bar' ||
      kind === 'stacked-line' ||
      kind === 'stacked-area' ||
      kind === 'waterfall-stack' ||
      kind === 'bubble' ||
      kind === 'quadrant-bubble' ||
      kind === 'gantt'
    );
  }

  private renderPreview(): void {
    // Interactive widgets render from the template via SelfServiceWidgetComponent,
    // not from an ECharts option. No dataset / mapping validation applies.
    if (this.isWidgetPreview) {
      this.validationMessage = '';
      this.previewOption = null;
      this.generatedConfig = JSON.stringify(this.buildGeneratedConfig(), null, 2);
      return;
    }

    const dataset = this.state.dataset();
    if (!dataset) {
      return;
    }

    this.validationMessage = this.getValidationMessage();

    if (!this.isMappingComplete || this.validationMessage) {
      this.generatedConfig = '';
      this.previewOption = null;
      return;
    }

    const config = this.buildGeneratedConfig();
    this.generatedConfig = JSON.stringify(config, null, 2);

    const kind = this.selectedChart.previewKind;
    if (kind === 'kpi' || kind === 'table') {
      // KPI / table previews are rendered by the template, not ECharts.
      this.previewOption = null;
      return;
    }

    const titleText = this.chartStyle.titleOverride || this.chartName || this.selectedChart.label;
    this.currentShapeOptions = this.getShapeOptionsFromState();
    this.previewOption = this.chartOptionBuilder.buildChartOption(
      this.selectedChartType || '',
      this.mapping,
      this.selectedAggregation,
      dataset,
      this.chartStyle,
      this.seriesField,
      titleText,
      this.buildChartOptionContext(),
    );
    this.currentShapeOptions = null;
  }

  private buildGeneratedConfig(): Record<string, unknown> {
    const dataset = this.state.dataset();
    const widgetId = this.buildWidgetId(this.state.activeSavedChartId() || this.createId());
    return {
      widgetId,
      type: this.selectedChartType,
      title: this.chartStyle.titleOverride || `${this.selectedChart.label} from ${dataset?.fileName || 'uploaded data'}`,
      dataSource: 'uploaded-file',
      sourceType: dataset?.sourceType,
      rowCount: dataset?.rows.length || 0,
      mapping: this.mapping,
      seriesField: this.seriesField || undefined,
      aggregation: this.selectedChart?.supportsAggregation ? this.selectedAggregation : undefined,
      transform: this.selectedChart?.supportsAggregation ? `${this.selectedAggregation}-by-category` : 'raw-points',
      sortBy: this.sortBy || undefined,
      sortDir: this.sortBy ? this.sortDir : undefined,
      topN: this.topN ?? undefined,
      topNDirection: this.topN ? this.topNDirection : undefined,
      dateGranularity: this.dateGranularity !== 'none' ? this.dateGranularity : undefined,
      tooltipColumns: this.tooltipColumns.length ? [...this.tooltipColumns] : undefined,
      comparisonColumn: this.comparisonColumn || undefined,
      referenceValue: this.referenceValue ?? undefined,
      filterKey: this.filterKey,
      filterListener: this.mergeListenerWidgetIds(this.filterListener, this.filterListenerCustom, widgetId),
      style: { ...this.chartStyle },
      // Carry widget settings so the generated/published config reproduces the
      // interactive widget exactly (only present for widget chart types).
      widgetConfig: this.isWidgetPreview ? { ...this.widgetConfig } : undefined,
    };
  }

  private buildWidgetId(chartId: string): string {
    return chartId.startsWith('widget-') ? chartId : `widget-${chartId}`;
  }

  private createDefaultChartName(fileName: string): string {
    const baseName = fileName.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
    return `${this.selectedChart.label} - ${baseName || 'Uploaded data'}`;
  }

  private createId(): string {
    return `chart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private createDefaultStyle(): ChartStyle {
    const builtIn: ChartStyle = {
      titleOverride: '',
      showLegend: true,
      legendPosition: 'bottom',
      showLabels: false,
      labelMode: 'value',
      palette: 'default',
      xAxisLabel: '',
      yAxisLabel: '',
      xAxisLabel2: '',
      yAxisLabel2: '',
      quadrantTopLeftLabel: '',
      quadrantTopRightLabel: '',
      quadrantBottomLeftLabel: '',
      quadrantBottomRightLabel: '',
      numberFormat: 'plain',
      numberScale: 'none',
      decimals: 0,
      numberFormat2: 'plain',
      numberScale2: 'none',
      decimals2: 0,
      lineShape: 'curved',
      comparisonValue: null,
      tableRowLimit: 50,
      tooltipEnabled: true,
      tooltipTrigger: 'auto',
      tooltipBackground: '#1e293b',
      tooltipTextColor: '#f8fafc',
      tooltipBorderColor: '#334155',
      tooltipBorderRadius: 6,
      tooltipFontSize: 12,
      tooltipAxisPointer: 'line',
      tooltipDecimals: 0,
    };
    // JSON `defaults.style` (if any) overrides matching style fields on open.
    return { ...builtIn, ...((this.layout.defaults?.style as Partial<ChartStyle> | undefined) ?? {}) };
  }

  private normalizeStyle(style?: Partial<ChartStyle> | null): ChartStyle {
    return { ...this.createDefaultStyle(), ...(style || {}) };
  }

  /** Snapshot of the component state the chart-option builder needs. Read the
   *  current shape options (set just before each render) plus the available
   *  chart types and aggregation definitions. */
  private buildChartOptionContext(): ChartOptionContext {
    return {
      chartTypes: this.state.chartTypes(),
      shapeOptions: this.currentShapeOptions,
      customAggregations: this.state.customAggregations(),
      builtinAggregations: this.builtinAggregations,
    };
  }

  private cloneDataset(dataset: UploadedDataset): UploadedDataset {
    return {
      fileName: dataset.fileName,
      sourceType: dataset.sourceType,
      columns: dataset.columns.map((column) => ({ ...column })),
      rows: dataset.rows.map((row) => ({ ...row })),
    };
  }

  private getValidationMessage(): string {
    if (!this.isMappingComplete || !this.state.dataset()) {
      return '';
    }

    // count / distinctCount aggregate any column type — no numeric check needed.
    const countBased = ['count', 'distinctCount'].includes(this.selectedAggregation);
    if (countBased) {
      return '';
    }

    const numericMappings = this.getRequiredNumericMappings();
    const invalidMapping = numericMappings.find((key) => {
      const columnName = this.mapping[key];
      return !!columnName && this.getColumnType(columnName) !== 'number';
    });

    if (!invalidMapping) {
      return '';
    }

    return `${this.mappingLabel(invalidMapping)} must be mapped to a numeric column for ${this.selectedChart.label}.`;
  }

  private getRequiredNumericMappings(): MappingKey[] {
    const previewKind = this.selectedChart?.previewKind;
    if (previewKind === 'table' || previewKind === 'kpi') {
      return [];
    }
    if (previewKind === 'scatter' || previewKind === 'bubble' || previewKind === 'gantt') {
      return ['xAxis', 'yAxis'];
    }
    if (this.selectedChart?.requiredMappings.includes('value')) {
      return ['value'];
    }
    return ['yAxis'];
  }

  private getColumnType(columnName: string): ColumnType | undefined {
    return this.state.dataset()?.columns.find((column) => column.name === columnName)?.type;
  }

  mappingLabel(key: MappingKey): string {
    const previewKind = this.currentPreviewKind;
    if (previewKind === 'gantt') {
      if (key === 'xAxis') return 'Start value (numeric)';
      if (key === 'yAxis') return 'End value (numeric)';
    }
    if (previewKind === 'scatter' || previewKind === 'bubble') {
      if (key === 'xAxis') return 'X-axis (numeric)';
    }
    const labels: Record<MappingKey, string> = {
      xAxis: 'X-axis',
      yAxis: 'Y-axis / value',
      category: 'Category',
      value: 'Value',
      xAxis2: 'Secondary X-axis',
      yAxis2: 'Secondary Y-axis',
      size: 'Size (numeric)',
      colorBy: 'Color by',
    };

    return labels[key];
  }

  columnsForMapping(_key: MappingKey): DatasetColumn[] {
    return this.allColumns;
  }

  // ── Cross-stage collaborators ───────────────────────────────────────────────
  // Persistence/config goes through the shared persistence service; pure
  // dashboard actions (render/add/navigate) are raised as events to the shell.

  /** Persist the saved-charts list to local storage (shared service). */
  private persistSavedCharts(charts: SavedSelfServiceChart[]): boolean {
    return this.persistence.persistSavedCharts(charts);
  }

  /** A saved chart changed — ask the shell to refresh the dashboard config/preview. */
  private updateDashboardConfig(): void {
    this.dashboardNeedsRefresh.emit();
  }

  /** A saved chart changed — ask the shell to re-render the dashboard grid. */
  private scheduleDashboardRender(): void {
    this.dashboardNeedsRefresh.emit();
  }

  /** Re-persist every saved dashboard that uses the edited chart (shared service). */
  private async resaveDashboardsForChart(chartId: string): Promise<void> {
    await this.persistence.resaveDashboardsForChart(chartId);
  }

  /** Remove any dashboard draft items that reference a deleted chart, then
   *  persist the pruned draft and ask the shell to refresh. */
  private removeDashboardItemsForChart(chartId: string): void {
    const next = this.state.dashboardItems().filter((item) => item.chartId !== chartId);
    if (next.length !== this.state.dashboardItems().length) {
      this.state.setDashboardItems(next);
      this.persistence.persistDashboardDraft(next);
      this.dashboardNeedsRefresh.emit();
    }
  }

  /** Ask the shell to add a chart to the dashboard draft. */
  private addChartToDashboard(chart: SavedSelfServiceChart): void {
    this.addToDashboard.emit(chart);
  }

  /** Ask the shell to switch the active view. */
  setViewMode(mode: 'data' | 'charts' | 'dashboards'): void {
    this.viewModeChange.emit(mode);
  }
}


